import { useEffect, useState } from 'react';
import { Tabs } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import TabletHeader from '../../components/Operations/TabletHeader';
import { TABLET_TITLE_STYLE, TABLET_BACK_BUTTON_STYLE } from '../../components/Operations/tabletStyles';
import { TabletSidebar } from '../../components/KitStaging/TabletSidebar';
import { useKitCardActions } from '../../components/KitStaging/useKitCardActions';
import { useListTrackedCardsQuery, useListReturnableQuery } from '../../redux/api/kit-staging';
import { OliGroup, groupByOli } from './boards/groupByOli';
import { UnassignedBoard } from './boards/UnassignedBoard';
import { CartBoard } from './boards/CartBoard';
import { MSABoard } from './boards/MSABoard';
import { JSABoard } from './boards/JSABoard';
import { DekitBoard } from './boards/DekitBoard';
import { ReturnableBoard } from './boards/ReturnableBoard';
import BadgerSetupOverlay from '../../components/KitStaging/celebration/SetupOverlay';
import BadgerErrorBoundary from '../../components/KitStaging/celebration/SafeWrapper';

type BoardKey = 'unassigned' | 'cart' | 'msa' | 'jsa' | 'dekit' | 'returnable';

const BOARDS: { key: BoardKey; label: string }[] = [
	{ key: 'unassigned', label: 'Unassigned' },
	{ key: 'cart', label: 'Cart' },
	{ key: 'msa', label: 'MSA' },
	{ key: 'jsa', label: 'JSA' },
	{ key: 'dekit', label: 'Dekit' },
	{ key: 'returnable', label: 'Return Kit' },
];

const DEFAULT_BOARD: BoardKey = 'jsa';

interface BoardProps {
	selectedGroup: OliGroup | null;
	onSelect: (g: OliGroup | null) => void;
}

function renderBoard(board: BoardKey, p: BoardProps) {
	switch (board) {
		case 'unassigned':
			return <UnassignedBoard {...p} />;
		case 'cart':
			return <CartBoard {...p} />;
		case 'msa':
			return <MSABoard {...p} />;
		case 'jsa':
			return <JSABoard {...p} />;
		case 'dekit':
			return <DekitBoard {...p} />;
		case 'returnable':
			return <ReturnableBoard {...p} />;
	}
}

/** Kit Staging tablet landing — the KT shop-floor layout: a persistent left
 *  sidebar (card detail + move actions, or sync/legend when idle) beside the
 *  active board. The board comes from the `:board` route param
 *  (operations/kit-staging/<board>); with no param it falls back to JSA.
 *  Switching a tab navigates so each board is deep-linkable (a tablet can be
 *  pinned to e.g. /operations/kit-staging/jsa).
 *
 *  Card selection is owned here and shared across every board: tapping a tile
 *  on any board populates the one sidebar, mirroring the standalone KT tablet
 *  (where the detail/action panel lives in the left rail, not a pop-out
 *  drawer). The move/confirm/undo/delete/mark-picked-up mutations are owned by
 *  `useKitCardActions` and fed to the sidebar. */
const KitStagingTabletPage = () => {
	const navigate = useNavigate();
	const { board } = useParams<{ board?: string }>();
	const active = (BOARDS.find((b) => b.key === board)?.key ?? DEFAULT_BOARD) as BoardKey;

	const [selectedGroup, setSelectedGroup] = useState<OliGroup | null>(null);
	const [showSetup, setShowSetup] = useState(false);
	const actions = useKitCardActions({
		onAfterDelete: () => setSelectedGroup(null),
		onCelebrateSetup: () => setShowSetup(true),
	});

	// Per-board OLI counts for the tab labels.
	const { data: cards } = useListTrackedCardsQuery({}, { pollingInterval: 10000 });
	const { data: returnable } = useListReturnableQuery(undefined, { pollingInterval: 10000 });
	const groups = groupByOli(cards ?? []);
	// Re-derive the selected group from the freshest cards every render, so the
	// sidebar reflects a move the instant it happens (the mutation invalidates
	// the TrackedCard list → refetch) rather than only after the 10s poll or a
	// manual refresh. The stored selection is just an OLI handle; data is live.
	// Fall back to the stored selection when the OLI isn't in `groups`: the main
	// tracked-cards list excludes soft-hidden cards, so a Return Kit card at the
	// machine would otherwise resolve to null and its sidebar would never open.
	const liveSelectedGroup = selectedGroup
		? (groups.find((g) => g.oli === selectedGroup.oli) ?? selectedGroup)
		: null;
	const countFor = (key: BoardKey): number => {
		if (key === 'returnable') {
			return new Set((returnable ?? []).map((c) => c.order_line_item_id)).size;
		}
		return groups.filter((g) => g.kitCard?.current_area === key || g.materialCard?.current_area === key).length;
	};

	// Drop the selected card whenever the active board changes — its detail no
	// longer belongs to what's on screen.
	useEffect(() => {
		setSelectedGroup(null);
	}, [active]);

	return (
		<div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100vh', overflow: 'hidden' }}>
			<div style={{ padding: '0 16px', flexShrink: 0 }}>
				<TabletHeader
					title="Kit Tracking"
					titleStyle={TABLET_TITLE_STYLE}
					showOnClickButton
					icon={<ArrowLeftOutlined />}
					buttonText="Back"
					actionButtonStyle={TABLET_BACK_BUTTON_STYLE}
					handleOnClick={() => navigate('/tablet/operations')}
				/>
			</div>
			<div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
				<TabletSidebar
					selectedGroup={liveSelectedGroup}
					lastSyncedAt={actions.lastSyncedAt}
					stagingAreas={actions.stagingAreas}
					activeTab={active}
					onInitiate={actions.onInitiate}
					onConfirm={actions.onConfirm}
					onUndo={actions.onUndo}
					onDelete={actions.onDelete}
					onMarkPickedUp={actions.onMarkPickedUp}
				/>
				<div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
					<Tabs
						activeKey={active}
						onChange={(key) => navigate(`/tablet/operations/kit-staging/${key}`)}
						style={{ padding: '0 16px' }}
						items={BOARDS.map((b) => ({ key: b.key, label: `${b.label} (${countFor(b.key)})` }))}
					/>
					{renderBoard(active, { selectedGroup: liveSelectedGroup, onSelect: setSelectedGroup })}
				</div>
			</div>
			{showSetup && (
				<BadgerErrorBoundary>
					<BadgerSetupOverlay onComplete={() => setShowSetup(false)} />
				</BadgerErrorBoundary>
			)}
		</div>
	);
};

export default KitStagingTabletPage;
